import { Caution, Flex, HStack, Icon, Text } from '@cad-arc';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import { DexQueryView, DexResultView } from '@mark43/dex-api';
import { getArcHitColor, getIconForDexEntityType } from '../utils';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { Tooltip } from '~/common/components/tooltip';

export function ResultHeader({
    query,
    result,
    isHit,
    children,
}: {
    query: DexQueryView;
    isHit: boolean;
    result?: DexResultView;
    children?: ReactNode;
}): React.ReactElement {
    const { userProfileView } = useBootstrapState();
    const isQueryByCurrentUser = query.createdBy === userProfileView.userId;
    const formattedCreatedDate = format(
        new Date(result ? result.createdDateUtc : query.createdDateUtc),
        'MM/dd/y HH:mm:ss'
    );
    const formattedDateAndUnit = `${formattedCreatedDate}${
        query.requestingUnitCallSign && isQueryByCurrentUser
            ? ` • Unit ${query.requestingUnitCallSign}`
            : ''
    }`;
    return (
        <Flex
            w="100%"
            style={{
                background: isHit
                    ? 'var(--arc-colors-negative-accent) 16%'
                    : 'var(--arc-colors-border-default)',
            }}
            borderColor={isHit ? 'raw.red.500' : 'border.default'}
            borderWidth="1px"
            borderStyle="solid"
            borderRadius="6px"
            padding="var(--arc-space-2) var(--arc-space-4)"
            justifyContent="space-between"
            alignItems="center"
        >
            <HStack>
                <Icon
                    icon={getIconForDexEntityType(query.dexEntityType)}
                    size="md"
                    style={{
                        color: getArcHitColor(isHit)
                    }}
                />
                <Flex direction="column" paddingLeft="4px">
                    <HStack>
                        <Text
                            variant="headingSm"
                            style={{ color: getArcHitColor(isHit), overflowWrap: 'anywhere' }}
                        >
                            {query.formattedQuery}
                        </Text>
                        {result?.drilldownTitle && result.drilldownIdentifier ? (
                            <Text style={{ color: getArcHitColor(isHit), fontSize: '16px' }}>
                                {result.drilldownTitle}: {result.drilldownIdentifier}
                            </Text>
                        ) : undefined}
                        {isHit && <Icon icon="ExclamationPoint" color="raw.red.500" />}
                    </HStack>
                    <HStack justifyContent="space-between" w="100%">
                        <Text variant="caption" color="secondary">
                            {formattedDateAndUnit}
                        </Text>
                        {query.requestingUnitCallSign && !isQueryByCurrentUser ? (
                            <Tooltip overlay={`Run by ${query.requestingUnitCallSign}`}>
                                <Caution priority={4} size="sm">
                                    Unit {query.requestingUnitCallSign}
                                </Caution>
                            </Tooltip>
                        ) : undefined}
                    </HStack>
                </Flex>
            </HStack>
            {children}
        </Flex>
    );
}
